import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  TextField,
  Button,
  Grid,
  Typography,
  Alert,
  IconButton,
  Tooltip,
  Paper
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import InfoIcon from '@mui/icons-material/Info';
import ModelSelectionBrowser from './ModelSelectionBrowser';

const GlobalModelConfigurationForm = ({ configuration, availableModels, onSave, onCancel, saving }) => {
  const [formData, setFormData] = useState({
    model1Id: '',
    model2Id: '',
    model3Id: '',
    model1Temperature: 0.0,
    model2Temperature: 0.0,
    model3Temperature: 0.0,
    model1MaxTokens: null,
    model2MaxTokens: null,
    model3MaxTokens: null,
    model1TimeoutSeconds: 300,
    model2TimeoutSeconds: 300,
    model3TimeoutSeconds: 300
  });

  const [currentModelSelection, setCurrentModelSelection] = useState(null);
  const [browserOpen, setBrowserOpen] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (configuration) {
      setFormData({
        model1Id: configuration.model1Id || '',
        model2Id: configuration.model2Id || '',
        model3Id: configuration.model3Id || '',
        model1Temperature: configuration.model1Temperature ?? 0.0,
        model2Temperature: configuration.model2Temperature ?? 0.0,
        model3Temperature: configuration.model3Temperature ?? 0.0,
        model1MaxTokens: configuration.model1MaxTokens ?? null,
        model2MaxTokens: configuration.model2MaxTokens ?? null,
        model3MaxTokens: configuration.model3MaxTokens ?? null,
        model1TimeoutSeconds: configuration.model1TimeoutSeconds ?? 300,
        model2TimeoutSeconds: configuration.model2TimeoutSeconds ?? 300,
        model3TimeoutSeconds: configuration.model3TimeoutSeconds ?? 300
      });
    }
  }, [configuration]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const validate = () => {
    const newErrors = {};

    // Validate model IDs
    if (!formData.model1Id) newErrors.model1Id = 'Model 1 is required';
    if (!formData.model2Id) newErrors.model2Id = 'Model 2 is required';
    if (!formData.model3Id) newErrors.model3Id = 'Model 3 is required';

    // Validate temperatures
    ['model1Temperature', 'model2Temperature', 'model3Temperature'].forEach(field => {
      const value = formData[field];
      if (value !== null && value !== undefined && (value < 0 || value > 1)) {
        newErrors[field] = 'Temperature must be between 0.0 and 1.0';
      }
    });

    // Validate max tokens
    ['model1MaxTokens', 'model2MaxTokens', 'model3MaxTokens'].forEach(field => {
      const value = formData[field];
      if (value !== null && value !== undefined && value !== '' && value <= 0) {
        newErrors[field] = 'Max tokens must be a positive integer';
      }
    });

    // Validate timeouts
    ['model1TimeoutSeconds', 'model2TimeoutSeconds', 'model3TimeoutSeconds'].forEach(field => {
      const value = formData[field];
      if (value !== null && value !== undefined && value <= 0) {
        newErrors[field] = 'Timeout must be a positive integer';
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validate()) {
      // Convert empty strings to null for optional fields
      const dataToSave = {
        ...formData,
        model1MaxTokens: formData.model1MaxTokens === '' ? null : formData.model1MaxTokens,
        model2MaxTokens: formData.model2MaxTokens === '' ? null : formData.model2MaxTokens,
        model3MaxTokens: formData.model3MaxTokens === '' ? null : formData.model3MaxTokens
      };
      
      // Include the id if configuration exists (for updates)
      if (configuration?.id) {
        dataToSave.id = configuration.id;
      }
      
      onSave(dataToSave);
    }
  };

  const getModelName = (modelId) => {
    const model = availableModels.find(m => m.id === modelId);
    return model ? model.name : modelId;
  };

  const handleBrowseModel = (modelNumber) => {
    setCurrentModelSelection(modelNumber);
    setBrowserOpen(true);
  };

  const handleModelSelect = (model) => {
    const modelIdField = `model${currentModelSelection}Id`;
    const tempField = `model${currentModelSelection}Temperature`;
    const maxTokensField = `model${currentModelSelection}MaxTokens`;

    // Ensure all values are defined (never undefined)
    const newTemperature = model.recommendedTemperature !== undefined && model.recommendedTemperature !== null
      ? model.recommendedTemperature
      : formData[tempField];
    
    const newMaxTokens = model.defaultMaxTokens !== undefined && model.defaultMaxTokens !== null
      ? model.defaultMaxTokens
      : (formData[maxTokensField] !== undefined ? formData[maxTokensField] : null);

    setFormData(prev => ({
      ...prev,
      [modelIdField]: model.id || '', // Use id property (contains Bedrock model ID or inference profile ID)
      [tempField]: newTemperature,
      [maxTokensField]: newMaxTokens
    }));

    setBrowserOpen(false);
    setCurrentModelSelection(null);
  };

  const renderModelSection = (modelNumber) => {
    const modelIdField = `model${modelNumber}Id`;
    const tempField = `model${modelNumber}Temperature`;
    const maxTokensField = `model${modelNumber}MaxTokens`;
    const timeoutField = `model${modelNumber}TimeoutSeconds`;

    return (
      <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">Model {modelNumber}</Typography>
          <Tooltip title="Select a model from the approved models list">
            <IconButton size="small" sx={{ ml: 1 }}>
              <InfoIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Model Name"
              value={formData[modelIdField] ? getModelName(formData[modelIdField]) : ''}
              disabled
              helperText="Display name of the selected model"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                fullWidth
                label="Model ID"
                value={formData[modelIdField] || ''}
                error={!!errors[modelIdField]}
                helperText={errors[modelIdField] || 'AWS Bedrock model identifier'}
                required
                disabled
              />
              <Button
                variant="outlined"
                startIcon={<SearchIcon />}
                onClick={() => handleBrowseModel(modelNumber)}
                sx={{ minWidth: 120 }}
              >
                Browse
              </Button>
            </Box>
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Temperature"
              type="number"
              value={formData[tempField] ?? 0.0}
              onChange={(e) => handleChange(tempField, parseFloat(e.target.value) || 0.0)}
              error={!!errors[tempField]}
              helperText={errors[tempField] || '0.0 = deterministic, 1.0 = creative'}
              inputProps={{ min: 0, max: 1, step: 0.1 }}
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Max Tokens"
              type="number"
              value={formData[maxTokensField] ?? ''}
              onChange={(e) => handleChange(maxTokensField, e.target.value === '' ? null : parseInt(e.target.value) || null)}
              error={!!errors[maxTokensField]}
              helperText={errors[maxTokensField] || 'Leave empty for model default'}
              inputProps={{ min: 1 }}
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Timeout (seconds)"
              type="number"
              value={formData[timeoutField] ?? 300}
              onChange={(e) => handleChange(timeoutField, parseInt(e.target.value) || 300)}
              error={!!errors[timeoutField]}
              helperText={errors[timeoutField]}
              inputProps={{ min: 1 }}
            />
          </Grid>
        </Grid>
      </Paper>
    );
  };

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Alert severity="info" sx={{ mb: 3 }}>
        Configure the three AI models used for invoice extraction. All customers will use this configuration.
      </Alert>

      {renderModelSection(1)}
      {renderModelSection(2)}
      {renderModelSection(3)}

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
        <Button onClick={onCancel} disabled={saving}>
          Cancel
        </Button>
        <Button type="submit" variant="contained" disabled={saving}>
          {saving ? 'Saving...' : 'Save Configuration'}
        </Button>
      </Box>

      <ModelSelectionBrowser
        open={browserOpen}
        models={availableModels}
        onSelect={handleModelSelect}
        onClose={() => {
          setBrowserOpen(false);
          setCurrentModelSelection(null);
        }}
      />
    </Box>
  );
};

GlobalModelConfigurationForm.propTypes = {
  configuration: PropTypes.object,
  availableModels: PropTypes.array.isRequired,
  onSave: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  saving: PropTypes.bool
};

export default GlobalModelConfigurationForm;
